import React, { useState } from 'react';
import { Modal } from '../ui/Modal';
import type { Campaign } from '../../types/campaign';
import { duplicateCampaign } from '../../services/campaigns';
import toast from 'react-hot-toast';

interface DuplicateCampaignModalProps {
  campaign: Campaign | null;
  open: boolean;
  onClose: () => void;
  onSuccess: (newCampaign: Campaign) => void;
}

export const DuplicateCampaignModal: React.FC<DuplicateCampaignModalProps> = ({
  campaign,
  open,
  onClose,
  onSuccess,
}) => {
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  React.useEffect(() => {
    if (open && campaign) {
      setName(`${campaign.name} (Copy)`);
    }
  }, [open, campaign]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!campaign) return;

    if (!name.trim()) {
      toast.error('Please enter a campaign name');
      return;
    }

    setIsSubmitting(true);
    try {
      const newCampaign = await duplicateCampaign(campaign.id, name);
      toast.success('Campaign duplicated successfully');
      onSuccess(newCampaign);
      onClose();
    } catch (error: any) {
      console.error('Failed to duplicate campaign:', error);
      const errorMessage =
        error.response?.data?.message || 'Failed to duplicate campaign';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setName('');
      onClose();
    }
  };

  return (
    <Modal open={open} onClose={handleClose} title="Duplicate Campaign">
      <form onSubmit={handleSubmit} className="space-y-4">
        {campaign && (
          <div className="bg-gray-50 p-3 rounded-md">
            <p className="text-sm text-gray-600">Duplicating:</p>
            <p className="font-medium text-gray-900">{campaign.name}</p>
          </div>
        )}

        <div>
          <label htmlFor="campaign-name" className="block text-sm font-medium text-gray-700 mb-1">
            New Campaign Name
          </label>
          <input
            id="campaign-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            placeholder="Enter campaign name"
            disabled={isSubmitting}
            autoFocus
          />
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || !name.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Duplicating...' : 'Duplicate Campaign'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

DuplicateCampaignModal.displayName = 'DuplicateCampaignModal';
